import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { IconAlignmentCenter } from 'central-icons/IconAlignmentCenter';
import { IconAlignmentLeft } from 'central-icons/IconAlignmentLeft';
import { IconAlignmentRight } from 'central-icons/IconAlignmentRight';
import { IconArrowDown } from 'central-icons/IconArrowDown';
import { IconArrowUp } from 'central-icons/IconArrowUp';
import { IconPencil } from 'central-icons/IconPencil';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import { IconSquareArrowTopRight } from 'central-icons/IconSquareArrowTopRight';
import { IconTrashCan } from 'central-icons/IconTrashCan';
import classNames from 'classnames';
import {
  Fragment,
  lazy,
  type ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { PagePaths } from '@revelio/core';
import { Button, ConfirmDialog, IconButton } from '@revelio/ui';

import { useReportDocument } from '../../report-document/report-document-context';
import { ChartProvider } from '../Chart/ChartProvider';
import { SpecialChartSelector } from '../Chart/ChartRoot/SpecialChart';
import { ChartMenuDataProvider } from '../Chart/chart-menu/ChartMenuContext';
import { usePageExport } from '../Chart/chart-menu/PageExportProvider';
import { ChartBuilderPanel } from '../ChartConfig/ChartBuilderPanel';
import { DashboardErrorBoundary } from '../DashboardErrorBoundary';
import type {
  ItemAlign,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import { useCoverSelectedParameters } from '../Reports/useCoverSelectedParameters';
import { TabVisibilityProvider } from '../TabVisibilityContext';
import {
  blockLeaves,
  htmlToMarkdown,
  isHtmlContent,
  REPORT_FLOW_COVER_ANCHOR_ID,
  type ResolvedLayoutPage,
  resolveLayoutPages,
} from './convert-grid-report-to-flow';
import { ReportFlowPptHost } from './ppt/ReportFlowPptHost';
import { ReportFlowPrintHost } from './print/ReportFlowPrintHost';
import styles from './ReportFlow.module.css';
import { ReportFlowBlockView } from './ReportFlowBlockView';
import { ReportFlowCoverParameters } from './ReportFlowCoverParameters';
import { ReportFlowHeaderContent } from './ReportFlowDocumentView';
import {
  acceptsSideDrop,
  DraggableBlock,
  DraggableColumnLeaf,
  dropCollisionDetection,
  EDITOR_AUTO_SCROLL,
  GapDropZone,
  isSingleItemDrag,
  PageSkeletons,
  ReportFlowDragGhost,
  resolveDropAction,
  useHoverReveal,
} from './ReportFlowEditorDnd';
import {
  ReportFlowCellResize,
  ReportFlowLeafFrame,
  type ReportFlowResizeCommit,
} from './ReportFlowResize';
import { ReportFlowToc } from './ReportFlowToc';
import {
  MAX_REPORT_FLOW_COLUMNS,
  pageItems,
  type ReportFlowAction,
} from './report-flow-reducer';
import {
  useReportFlowScrollAnchor,
  useReportFlowScrollSpy,
} from './report-flow-scroll';
import type { ReportFlowBlock } from './report-flow-types';

// The modal brings in CodeMirror and the Handlebars language support, so it
// loads when a report is edited rather than at startup.
const loadTextContentEditorModal = () =>
  import('../Chart/TextChart/TextContentEditorModal');

const TextContentEditorModal = lazy(() =>
  loadTextContentEditorModal().then((module) => ({
    default: module.TextContentEditorModal,
  }))
);

const DEFAULT_TEXT_CONTENT = '## New section\n\nAdd your text here.';

/** Hairline between pages; the chip waits for a long hover unless `always`. */
const AddPageDivider = ({
  label,
  always = false,
  onAdd,
}: {
  label: string;
  always?: boolean;
  onAdd: () => void;
}) => {
  const { revealed, hoverProps } = useHoverReveal(!always);
  return (
    <div
      className={classNames(
        styles.addPageRow,
        (always || revealed) && styles.addPageRowRevealed
      )}
      data-testid="report-flow-add-page"
      {...hoverProps}
    >
      <button
        type="button"
        className={styles.addPageButton}
        aria-label={label}
        onClick={onAdd}
      >
        <IconPlusSmall size={14} />
        Add page
      </button>
    </div>
  );
};

const blockItemIds = (block: ReportFlowBlock): string[] =>
  blockLeaves(block).map((leaf) => leaf.item.id);

type DeleteCandidate =
  | { kind: 'block'; block: ReportFlowBlock }
  | {
      kind: 'page';
      page: ResolvedLayoutPage;
      pageNumber: number;
      pageCount: number;
    };

const candidateItemIds = (candidate: DeleteCandidate): string[] =>
  candidate.kind === 'page'
    ? candidate.page.blocks.flatMap(blockItemIds)
    : blockItemIds(candidate.block);

const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

const pageContentsSummary = (page: ResolvedLayoutPage): string | null => {
  const leaves = page.blocks.flatMap(blockLeaves);
  const charts = leaves.filter((leaf) => leaf.kind === 'chart').length;
  const texts = leaves.filter((leaf) => leaf.kind === 'text').length;
  const images = leaves.filter((leaf) => leaf.kind === 'image').length;
  const parts = [
    charts > 0 && pluralize(charts, 'chart', 'charts'),
    texts > 0 && pluralize(texts, 'text block', 'text blocks'),
    images > 0 && pluralize(images, 'image', 'images'),
  ].filter((part): part is string => typeof part === 'string');
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0] ?? null;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

const deleteCandidateTitle = (candidate: DeleteCandidate | null): string => {
  if (candidate?.kind === 'page') {
    return `Delete page ${candidate.pageNumber} of ${candidate.pageCount}?`;
  }
  if (candidate?.kind === 'block' && candidate.block.kind === 'columns') {
    return 'Delete this container?';
  }
  return 'Delete this block?';
};

const deleteCandidateMessage = (candidate: DeleteCandidate | null): string => {
  if (
    !candidate ||
    (candidate.kind === 'block' && candidate.block.kind !== 'columns')
  ) {
    return 'This removes the block from the report.';
  }
  if (candidate.kind === 'page') {
    const contents = pageContentsSummary(candidate.page);
    return contents
      ? `This removes page ${candidate.pageNumber} and its ${contents} from the report.`
      : `This removes the empty page ${candidate.pageNumber} from the report.`;
  }
  const count = candidateItemIds(candidate).length;
  if (count === 0) return 'This removes the page from the report.';
  return `This removes its ${count} ${count === 1 ? 'block' : 'blocks'} from the report.`;
};

const ALIGN_OPTIONS: { align: ItemAlign; icon: ReactNode; label: string }[] = [
  { align: 'left', icon: <IconAlignmentLeft size={14} />, label: 'Align left' },
  {
    align: 'center',
    icon: <IconAlignmentCenter size={14} />,
    label: 'Align center',
  },
  {
    align: 'right',
    icon: <IconAlignmentRight size={14} />,
    label: 'Align right',
  },
];

const TextDraftPreview = ({
  item,
  draft,
}: {
  item: PositionedItem;
  draft: string;
}) => {
  const previewItem = useMemo(
    () => ({ ...item, plot: { ...item.plot, textHtml: draft } }),
    [item, draft]
  );
  return (
    <div className={styles.textLeaf}>
      <ChartProvider item={previewItem} variant="report">
        <ChartMenuDataProvider>
          <SpecialChartSelector />
        </ChartMenuDataProvider>
      </ChartProvider>
    </div>
  );
};

const ClickToEditText = ({
  onEdit,
  children,
}: {
  onEdit?: () => void;
  children: ReactNode;
}) => {
  if (!onEdit) return children;
  return (
    <div
      className={styles.textEditTarget}
      data-testid="report-flow-text-edit-target"
      onClick={(event) => {
        if ((event.target as Element).closest('a, button, input, textarea')) {
          return;
        }
        onEdit();
      }}
    >
      {children}
    </div>
  );
};

const hasConfiguredLeaf = (block: ReportFlowBlock) =>
  blockLeaves(block).some((leaf) => leaf.item.plot?.chartType !== undefined);

export const ReportFlowEditor = ({
  onBuilderOpenChange,
  readOnly = false,
}: {
  onBuilderOpenChange?: (isOpen: boolean) => void;
  readOnly?: boolean;
}) => {
  const {
    document: reportDocument,
    flow: doc,
    dispatch: dispatchDocument,
  } = useReportDocument();
  const coverParameters = useCoverSelectedParameters();
  const { printFlowReport, isExporting } = usePageExport();

  // Keyed by item id: the doc skips unconfigured items, so its block ids can differ from the editor's.
  const sectionByLeadItemId = useMemo(
    () =>
      new Map(
        doc.sections.flatMap((section, index) => {
          const [firstBlock] = section.blocks;
          const entry = { anchorId: section.anchorId, isFirst: index === 0 };
          return firstBlock
            ? blockItemIds(firstBlock).map((itemId) => [itemId, entry] as const)
            : [];
        })
      ),
    [doc]
  );

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { activeSectionId, updateActiveSection } = useReportFlowScrollSpy(
    doc,
    scrollAreaRef
  );
  const captureScrollAnchor = useReportFlowScrollAnchor(
    scrollAreaRef,
    readOnly
  );

  const dispatch = ({
    replacePreviousHistorySnapshot,
    ...action
  }: ReportFlowAction & { replacePreviousHistorySnapshot?: boolean }) =>
    dispatchDocument({
      type: 'FLOW',
      action: action as ReportFlowAction,
      ...(replacePreviousHistorySnapshot
        ? { replacePreviousHistorySnapshot: true }
        : {}),
    });

  const reportItems = useMemo(
    () => pageItems(reportDocument.pages),
    [reportDocument.pages]
  );
  const pages = useMemo(
    () => resolveLayoutPages(reportDocument.pages),
    [reportDocument.pages]
  );
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const pendingTextItemRef = useRef<string | null>(null);
  const editingItem =
    reportItems.find((item) => item.id === editingItemId) ?? null;
  const editingSource = editingItem?.plot?.textHtml ?? '';

  const [configItemId, setConfigItemId] = useState<string | null>(null);
  // One text edit at a time. The lazy modal covers nothing until its chunk
  // resolves, so Add text and block clicks must refuse a second edit themselves.
  const editText = (itemId: string) => {
    if (editingItem !== null) return;
    setEditingItemId(itemId);
    setConfigItemId(itemId);
  };
  const closeBuilder = () => setConfigItemId(null);
  const [deleteCandidate, setDeleteCandidate] =
    useState<DeleteCandidate | null>(null);
  const [pageDeleteHoverId, setPageDeleteHoverId] = useState<string | null>(
    null
  );
  const deleteTargetPageId =
    deleteCandidate?.kind === 'page'
      ? deleteCandidate.page.id
      : pageDeleteHoverId;
  const configItem =
    reportItems.find((item) => item.id === configItemId) ?? undefined;

  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const isDragging = activeDragId !== null;
  const isDraggingSingleItem =
    activeDragId !== null && isSingleItemDrag(pages, activeDragId);

  const titleEditRunRef = useRef(false);
  const descriptionEditRunRef = useRef(false);

  useEffect(() => {
    if (!readOnly) return;
    setConfigItemId(null);
    setEditingItemId(null);
    setDeleteCandidate(null);
  }, [readOnly]);

  // Fetch the chunk while the report is being edited so the first open does not
  // wait on it. Read-only views never render the modal, so they skip it. A
  // failed fetch is dropped here; opening the editor requests it again.
  useEffect(() => {
    if (readOnly) return;
    loadTextContentEditorModal().catch(() => undefined);
  }, [readOnly]);

  useEffect(() => {
    onBuilderOpenChange?.(configItemId !== null);
    return () => onBuilderOpenChange?.(false);
  }, [configItemId, onBuilderOpenChange]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const totalBlocks = pages.reduce((sum, page) => sum + page.blocks.length, 0);
  let blockOrdinal = 0;

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveDragId(null);
    if (!over) return;
    const action = resolveDropAction(pages, String(active.id), String(over.id));
    if (action) dispatch(action);
  };

  const handleMove = (
    pageId: string,
    blockIndex: number,
    direction: 'up' | 'down'
  ) => {
    dispatch({
      type: 'REPORT_FLOW_MOVE_BLOCK',
      payload: { pageId, blockIndex, direction },
    });
  };

  const handleDelete = (candidate: DeleteCandidate) => {
    if (candidate.kind === 'page') {
      dispatch({
        type: 'REPORT_FLOW_DELETE_PAGE',
        payload: { pageId: candidate.page.id },
      });
    } else {
      dispatchDocument({
        type: 'TAB_CONTENT',
        action: {
          type: 'DELETE_ITEMS',
          payload: { ids: blockItemIds(candidate.block) },
        },
      });
    }
    if (configItemId && candidateItemIds(candidate).includes(configItemId)) {
      setConfigItemId(null);
    }
  };

  const deleteTitle = deleteCandidateTitle(deleteCandidate);
  const deleteMessage = deleteCandidateMessage(deleteCandidate);

  const handleAddColumnChart = (
    pageId: string,
    blockIndex: number,
    columnIndex: number
  ) => {
    const itemId = crypto.randomUUID();
    dispatch({
      type: 'REPORT_FLOW_ADD_COLUMN_CHART',
      payload: { itemId, pageId, blockIndex, columnIndex },
    });
    setConfigItemId(itemId);
  };

  const handleAddText = (pageId: string, index: number) => {
    if (editingItem !== null) return;
    const itemId = crypto.randomUUID();
    dispatch({
      type: 'REPORT_FLOW_ADD_TEXT_ITEM',
      payload: { itemId, pageId, index, textHtml: DEFAULT_TEXT_CONTENT },
    });
    pendingTextItemRef.current = itemId;
    editText(itemId);
  };

  const handleAddChart = (pageId: string, index: number) => {
    const itemId = crypto.randomUUID();
    dispatch({
      type: 'REPORT_FLOW_ADD_CHART_ITEM',
      payload: { itemId, pageId, index },
    });
    setConfigItemId(itemId);
  };

  // A page rarely starts without a heading, so it opens with a text block.
  const handleAddPage = (at: { afterPageId?: string; atStart?: boolean }) => {
    const pageId = crypto.randomUUID();
    dispatch({
      type: 'REPORT_FLOW_ADD_PAGE',
      payload: { pageId, ...at },
    });
    dispatch({
      type: 'REPORT_FLOW_ADD_TEXT_ITEM',
      payload: {
        itemId: crypto.randomUUID(),
        pageId,
        index: 0,
        textHtml: DEFAULT_TEXT_CONTENT,
      },
      replacePreviousHistorySnapshot: true,
    });
  };

  const handleResizeCommit = (
    itemId: string,
    { width, height }: ReportFlowResizeCommit
  ) => {
    dispatch({
      type: 'REPORT_FLOW_RESIZE_ITEM',
      payload: { itemId, width, height },
    });
  };

  return (
    <TabVisibilityProvider isVisible>
      <div className={styles.flowContainer}>
        <div
          className={styles.viewport}
          data-testid={readOnly ? 'report-flow-view' : 'report-flow-editor'}
        >
          <ReportFlowToc
            doc={doc}
            activeSectionId={activeSectionId}
            scrollContainerRef={scrollAreaRef}
            variant={readOnly ? 'full' : 'compact'}
          />
          <div
            ref={scrollAreaRef}
            className={styles.scrollArea}
            onScroll={() => {
              updateActiveSection();
              captureScrollAnchor();
            }}
          >
            <div className={styles.document}>
              <header
                className={styles.header}
                id={REPORT_FLOW_COVER_ANCHOR_ID}
              >
                {readOnly ? (
                  <ReportFlowHeaderContent
                    doc={doc}
                    breadcrumb={{
                      label: 'Reports',
                      href: `/${PagePaths.REPORTS}`,
                      current: reportDocument.name,
                    }}
                    headerActions={
                      <Button
                        color="primary"
                        className={styles.viewPdfButton}
                        rightIcon={<IconSquareArrowTopRight size={18} />}
                        onClick={() => void printFlowReport()}
                        disabled={isExporting}
                        data-testid="report-flow-view-pdf"
                      >
                        View PDF
                      </Button>
                    }
                  />
                ) : (
                  <>
                    <input
                      className={styles.titleInput}
                      value={reportDocument.title ?? ''}
                      placeholder="Report title"
                      data-testid="report-flow-title-input"
                      onChange={(event) => {
                        dispatchDocument({
                          type: 'UPDATE_TITLE',
                          payload: event.target.value,
                          ...(titleEditRunRef.current
                            ? { replacePreviousHistorySnapshot: true }
                            : {}),
                        });
                        titleEditRunRef.current = true;
                      }}
                      onBlur={() => {
                        titleEditRunRef.current = false;
                      }}
                    />
                    <textarea
                      className={styles.descriptionInput}
                      value={reportDocument.description ?? ''}
                      placeholder="Report description"
                      rows={3}
                      data-testid="report-flow-description-input"
                      onChange={(event) => {
                        dispatchDocument({
                          type: 'UPDATE_DESCRIPTION',
                          payload: event.target.value,
                          ...(descriptionEditRunRef.current
                            ? { replacePreviousHistorySnapshot: true }
                            : {}),
                        });
                        descriptionEditRunRef.current = true;
                      }}
                      onBlur={() => {
                        descriptionEditRunRef.current = false;
                      }}
                    />
                  </>
                )}
                <ReportFlowCoverParameters parameters={coverParameters} />
              </header>

              <DndContext
                sensors={sensors}
                autoScroll={EDITOR_AUTO_SCROLL}
                collisionDetection={dropCollisionDetection}
                onDragStart={({ active }) => setActiveDragId(String(active.id))}
                onDragCancel={() => setActiveDragId(null)}
                onDragEnd={handleDragEnd}
              >
                {!readOnly && (
                  <AddPageDivider
                    label="Add page at the start"
                    onAdd={() => handleAddPage({ atStart: true })}
                  />
                )}

                {pages.map((page, pageIndex) => (
                  <Fragment key={page.id}>
                    <section
                      className={classNames(
                        readOnly ? styles.viewPage : styles.editorPage,
                        !readOnly &&
                          deleteTargetPageId === page.id &&
                          styles.editorPageDeleteTarget
                      )}
                      data-testid={
                        readOnly ? undefined : 'report-flow-editor-page'
                      }
                      data-delete-target={
                        !readOnly && deleteTargetPageId === page.id
                          ? true
                          : undefined
                      }
                    >
                      {!readOnly && (
                        <div className={styles.editorPageHeader}>
                          <span className={styles.editorPageLabel}>
                            Page {pageIndex + 1}
                          </span>
                          <IconButton
                            size="sm"
                            icon={<IconTrashCan size={14} />}
                            aria-label={`Delete page ${pageIndex + 1}`}
                            data-testid="report-flow-delete-page"
                            className={styles.editorPageAction}
                            disabled={pages.length <= 1}
                            onMouseEnter={() => setPageDeleteHoverId(page.id)}
                            onMouseLeave={() => setPageDeleteHoverId(null)}
                            onFocus={() => setPageDeleteHoverId(page.id)}
                            onBlur={() => setPageDeleteHoverId(null)}
                            onClick={() =>
                              setDeleteCandidate({
                                kind: 'page',
                                page,
                                pageNumber: pageIndex + 1,
                                pageCount: pages.length,
                              })
                            }
                          />
                        </div>
                      )}

                      {!readOnly && (
                        <GapDropZone
                          pages={pages}
                          pageId={page.id}
                          index={0}
                          isDragging={isDragging}
                          expandOnHover={page.blocks.length > 0}
                          onAddChart={(index) => handleAddChart(page.id, index)}
                          onAddText={(index) => handleAddText(page.id, index)}
                        />
                      )}

                      {page.blocks.map((block, blockIndex) => {
                        const ordinal = blockOrdinal;
                        blockOrdinal += 1;
                        if (readOnly && !hasConfiguredLeaf(block)) return null;
                        const section = blockItemIds(block)
                          .map((itemId) => sectionByLeadItemId.get(itemId))
                          .find((entry) => entry !== undefined);
                        const isTextLeaf = block.kind === 'text';
                        const isAlignable =
                          block.kind === 'chart' || block.kind === 'image';
                        const showSideDrops =
                          isDraggingSingleItem &&
                          acceptsSideDrop(pages, activeDragId, block.id);
                        return (
                          <Fragment key={block.id}>
                            <DraggableBlock
                              pages={pages}
                              blockId={block.id}
                              showSideDrops={showSideDrops}
                              readOnly={readOnly}
                              anchorId={section?.anchorId}
                              isSectionBreak={
                                section !== undefined && !section.isFirst
                              }
                              onActivate={
                                isTextLeaf
                                  ? () => editText(block.item.id)
                                  : undefined
                              }
                              toolbar={
                                readOnly ? null : (
                                  <div className={styles.editorBlockToolbar}>
                                    <IconButton
                                      size="sm"
                                      icon={<IconArrowUp size={14} />}
                                      aria-label="Move block up"
                                      disabled={ordinal === 0}
                                      onClick={() =>
                                        handleMove(page.id, blockIndex, 'up')
                                      }
                                    />
                                    <IconButton
                                      size="sm"
                                      icon={<IconArrowDown size={14} />}
                                      aria-label="Move block down"
                                      disabled={ordinal === totalBlocks - 1}
                                      onClick={() =>
                                        handleMove(page.id, blockIndex, 'down')
                                      }
                                    />
                                    {isAlignable &&
                                      ALIGN_OPTIONS.map(
                                        ({ align, icon, label }) => (
                                          <IconButton
                                            key={align}
                                            size="sm"
                                            icon={icon}
                                            aria-label={label}
                                            isActive={
                                              (block.item.align ?? 'left') ===
                                              align
                                            }
                                            data-testid={`report-flow-align-${align}`}
                                            onClick={() =>
                                              dispatch({
                                                type: 'REPORT_FLOW_SET_ITEM_ALIGN',
                                                payload: {
                                                  itemId: block.item.id,
                                                  align,
                                                },
                                              })
                                            }
                                          />
                                        )
                                      )}
                                    {isTextLeaf && (
                                      <IconButton
                                        size="sm"
                                        icon={<IconPencil size={14} />}
                                        aria-label="Edit text"
                                        data-testid="report-flow-edit-text"
                                        onClick={() => editText(block.item.id)}
                                      />
                                    )}
                                    <IconButton
                                      size="sm"
                                      icon={<IconTrashCan size={14} />}
                                      aria-label="Delete block"
                                      data-testid="report-flow-delete-block"
                                      onClick={() =>
                                        setDeleteCandidate({
                                          kind: 'block',
                                          block,
                                        })
                                      }
                                    />
                                  </div>
                                )
                              }
                            >
                              <ReportFlowBlockView
                                block={block}
                                onChartClick={
                                  readOnly ? undefined : setConfigItemId
                                }
                                leafWrapper={(leaf, content) =>
                                  leaf.kind === 'text' ? (
                                    <ClickToEditText
                                      onEdit={
                                        readOnly
                                          ? undefined
                                          : () => editText(leaf.item.id)
                                      }
                                    >
                                      {content}
                                    </ClickToEditText>
                                  ) : (
                                    <ReportFlowLeafFrame
                                      leaf={leaf}
                                      disabled={readOnly || isDragging}
                                      onCommit={handleResizeCommit}
                                    >
                                      {content}
                                    </ReportFlowLeafFrame>
                                  )
                                }
                                columnWrapper={(leaf, content) => {
                                  const columns =
                                    block.kind === 'columns'
                                      ? block.columns
                                      : [];
                                  const columnIndex = columns.findIndex(
                                    (column) => column.id === leaf.id
                                  );
                                  const canAddColumn =
                                    columns.length > 0 &&
                                    columns.length < MAX_REPORT_FLOW_COLUMNS;
                                  return (
                                    <DraggableColumnLeaf
                                      pages={pages}
                                      itemId={leaf.item.id}
                                      columnIndex={columnIndex}
                                      isLastColumn={
                                        columnIndex === columns.length - 1
                                      }
                                      readOnly={readOnly}
                                      isEditorDragging={isDragging}
                                      showSideDrops={showSideDrops}
                                      isSelected={configItemId === leaf.item.id}
                                      onSelect={
                                        leaf.kind === 'text'
                                          ? editText
                                          : setConfigItemId
                                      }
                                      onAddBefore={
                                        !readOnly &&
                                        canAddColumn &&
                                        columnIndex === 0
                                          ? () =>
                                              handleAddColumnChart(
                                                page.id,
                                                blockIndex,
                                                0
                                              )
                                          : undefined
                                      }
                                      onAddAfter={
                                        !readOnly && canAddColumn
                                          ? () =>
                                              handleAddColumnChart(
                                                page.id,
                                                blockIndex,
                                                columnIndex + 1
                                              )
                                          : undefined
                                      }
                                    >
                                      <ReportFlowCellResize
                                        leaf={leaf}
                                        columnWidths={columns.map(
                                          (column) => column.item.position.width
                                        )}
                                        columnIndex={columnIndex}
                                        disabled={readOnly || isDragging}
                                        onCommit={handleResizeCommit}
                                      >
                                        {content}
                                      </ReportFlowCellResize>
                                    </DraggableColumnLeaf>
                                  );
                                }}
                              />
                            </DraggableBlock>
                            {!readOnly && (
                              <GapDropZone
                                pages={pages}
                                pageId={page.id}
                                index={blockIndex + 1}
                                isDragging={isDragging}
                                expandOnHover={
                                  blockIndex + 1 < page.blocks.length
                                }
                                onAddChart={(index) =>
                                  handleAddChart(page.id, index)
                                }
                                onAddText={(index) =>
                                  handleAddText(page.id, index)
                                }
                              />
                            )}
                          </Fragment>
                        );
                      })}

                      {!readOnly && (
                        <PageSkeletons
                          pages={pages}
                          pageId={page.id}
                          isDragging={isDragging}
                          collapsible={page.blocks.length >= 2}
                          onAddChart={() =>
                            handleAddChart(page.id, page.blocks.length)
                          }
                          onAddText={() =>
                            handleAddText(page.id, page.blocks.length)
                          }
                        />
                      )}
                    </section>

                    {!readOnly && (
                      <AddPageDivider
                        label={`Add page after page ${pageIndex + 1}`}
                        always={pageIndex === pages.length - 1}
                        onAdd={() => handleAddPage({ afterPageId: page.id })}
                      />
                    )}
                  </Fragment>
                ))}

                <ReportFlowDragGhost
                  pages={pages}
                  activeDragId={activeDragId}
                />
              </DndContext>
            </div>
          </div>

          {!readOnly && (
            <>
              <ChartBuilderPanel
                isOpen={configItem !== undefined}
                item={configItem}
                selectedIds={configItem ? new Set([configItem.id]) : new Set()}
                clearSelection={closeBuilder}
                onClose={closeBuilder}
              />

              <ConfirmDialog
                open={deleteCandidate !== null}
                title={deleteTitle}
                confirmLabel={
                  deleteCandidate?.kind === 'page' ? 'Delete page' : 'Delete'
                }
                destructive
                onConfirm={() => {
                  if (deleteCandidate) handleDelete(deleteCandidate);
                  setDeleteCandidate(null);
                }}
                onCancel={() => setDeleteCandidate(null)}
              >
                {deleteMessage}
              </ConfirmDialog>

              {editingItem && (
                <DashboardErrorBoundary context="ReportFlowEditor text editor">
                  <Suspense fallback={null}>
                    <TextContentEditorModal
                      isOpen
                      onClose={() => {
                        if (pendingTextItemRef.current === editingItem.id) {
                          pendingTextItemRef.current = null;
                          dispatch({
                            type: 'REPORT_FLOW_DELETE_ITEM',
                            payload: { itemId: editingItem.id },
                            replacePreviousHistorySnapshot: true,
                          });
                        }
                        setEditingItemId(null);
                      }}
                      startingText={
                        isHtmlContent(editingSource)
                          ? htmlToMarkdown(editingSource)
                          : editingSource
                      }
                      onSave={(text) => {
                        pendingTextItemRef.current = null;
                        dispatchDocument({
                          type: 'TAB_CONTENT',
                          action: {
                            type: 'UPDATE_ITEM_TEXT_HTML',
                            payload: { id: editingItem.id, textHtml: text },
                          },
                        });
                      }}
                      renderPreview={(text) => (
                        <TextDraftPreview item={editingItem} draft={text} />
                      )}
                    />
                  </Suspense>
                </DashboardErrorBoundary>
              )}
            </>
          )}
          <ReportFlowPrintHost doc={doc} parameters={coverParameters} />
          <ReportFlowPptHost />
        </div>
      </div>
    </TabVisibilityProvider>
  );
};
